// Validation du profil voyageur, extraite en fonction PURE.
//
// Elle reprend exactement les règles de checkFormValidity (VoyageurInsert) et les
// messages de VoyageurUpdate, qui les portaient séparément. Testable sans React,
// et surtout : une seule source de vérité pour les deux écrans.

export interface ChampsVoyageur {
  nom: string;
  prenom: string;
  telephone: string;
  profession: string;
  etatCivil: string;
  /** 'oui' | 'non' | '' — l'adresse du voyageur est-elle celle du compte ? */
  adresseIdentique: string;
  adresse: string;
  codePostal: string;
  ville: string;
  photoUri: string | null;
  passeportUri: string | null;
}

export type ChampVoyageur = keyof ChampsVoyageur;

export interface ResultatValidation {
  valide: boolean;
  erreurs: Partial<Record<ChampVoyageur, string>>;
}

/** Libellés affichés dans le bloc « Il manque encore… ». */
export const LIBELLES: Record<ChampVoyageur, string> = {
  nom: 'Nom',
  prenom: 'Prénom',
  telephone: 'Téléphone',
  profession: 'Profession',
  etatCivil: 'État civil',
  adresseIdentique: 'Adresse du voyageur',
  adresse: 'Adresse',
  codePostal: 'Code postal',
  ville: 'Ville',
  photoUri: "Photo d'identité",
  passeportUri: 'Scan du passeport',
};

const vide = (v: string) => v.trim() === '';

/** Au moins un chiffre, et rien d'autre que chiffres, espaces, points, tirets, parenthèses et +. */
const TELEPHONE_ACCEPTE = /^[+()\d][\d\s.\-()]*$/;

export const validerVoyageur = (v: ChampsVoyageur): ResultatValidation => {
  const erreurs: Partial<Record<ChampVoyageur, string>> = {};

  if (vide(v.nom)) erreurs.nom = 'Le nom est obligatoire.';
  if (vide(v.prenom)) erreurs.prenom = 'Le prénom est obligatoire.';

  if (vide(v.telephone)) {
    erreurs.telephone = 'Le téléphone est obligatoire.';
  } else if (!TELEPHONE_ACCEPTE.test(v.telephone.trim())) {
    erreurs.telephone = 'Numéro invalide. Exemple : 06 12 34 56 78 ou +33 6 12 34 56 78.';
  } else if (chiffresSeuls(v.telephone).length < 6) {
    erreurs.telephone = 'Numéro trop court.';
  }

  if (vide(v.profession)) erreurs.profession = 'La profession est obligatoire.';
  if (vide(v.etatCivil)) erreurs.etatCivil = "Choisissez l'état civil.";

  if (vide(v.adresseIdentique)) {
    erreurs.adresseIdentique = "Indiquez si l'adresse du voyageur est la vôtre.";
  } else if (v.adresseIdentique === 'non') {
    if (vide(v.adresse)) erreurs.adresse = "L'adresse est obligatoire.";
    if (vide(v.codePostal)) erreurs.codePostal = 'Le code postal est obligatoire.';
    if (vide(v.ville)) erreurs.ville = 'La ville est obligatoire.';
  }

  if (!v.photoUri) erreurs.photoUri = "La photo d'identité est obligatoire.";
  if (!v.passeportUri) erreurs.passeportUri = 'Le scan du passeport est obligatoire.';

  return { valide: Object.keys(erreurs).length === 0, erreurs };
};

/** Ne conserve que les chiffres — ce qu'attend le backend. */
export const chiffresSeuls = (telephone: string): string => telephone.replace(/[^0-9]/g, '');

/** Liste ordonnée des champs manquants, pour le bloc de rappel. */
export const libellesChampsManquants = (
  erreurs: Partial<Record<ChampVoyageur, string>>
): string[] =>
  (Object.keys(LIBELLES) as ChampVoyageur[])
    .filter((champ) => erreurs[champ] !== undefined)
    .map((champ) => LIBELLES[champ]);
